import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, ChevronLeft, ChevronRight, Droplets, MapPin, Maximize2, MessageCircle, Sprout, X } from "lucide-react";
import { scrollToId } from "../lib/site";

const pad = (n) => String(n).padStart(2, "0");

export default function ProductModal({ product, onClose, onEnquire }) {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    setIdx(0);
    setFullscreen(false);
    if (product) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [product]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && (fullscreen ? setFullscreen(false) : onClose());
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, fullscreen]);

  const images = product?.images?.length ? product.images : [];
  const comingSoon = product?.availability === "coming_soon";
  const toConfirm = product?.availability === "to_confirm";
  const next = () => setIdx((i) => (i + 1) % images.length);
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);

  const onDragEnd = (_, info) => {
    if (info.offset.x < -60) next();
    else if (info.offset.x > 60) prev();
  };

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-6"
        >
          <div className="absolute inset-0 bg-nightgreen/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="product-detail-modal"
            initial={{ opacity: 0, y: 60, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-lenis-prevent
            className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-cream rounded-t-3xl sm:rounded-3xl shadow-2xl grid md:grid-cols-2"
          >
            <button
              data-testid="modal-close-btn"
              onClick={onClose}
              aria-label="Close product details"
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-nightgreen/60 text-cream flex items-center justify-center backdrop-blur hover:bg-nightgreen transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative bg-nightgreen">
              <div className="relative aspect-square md:aspect-auto md:h-full overflow-hidden touch-pan-y">
                {images.length > 0 && (
                  <motion.img
                    key={idx}
                    data-testid="modal-gallery-image"
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    src={images[idx].url}
                    alt={images[idx].alt || product.name}
                    className="w-full h-full object-cover cursor-zoom-in"
                    drag={images.length > 1 ? "x" : false}
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.18}
                    onDragEnd={onDragEnd}
                    onClick={() => setFullscreen(true)}
                  />
                )}
                <span
                  data-testid="modal-image-counter"
                  className="absolute bottom-3 right-3 rounded-md bg-nightgreen/70 text-cream text-xs font-medium tracking-widest px-2.5 py-1 backdrop-blur-sm"
                >
                  {pad(idx + 1)} / {pad(images.length)}
                </span>
                <button
                  data-testid="modal-fullscreen-btn"
                  onClick={() => setFullscreen(true)}
                  aria-label="View fullscreen"
                  className="absolute top-3 left-3 w-9 h-9 rounded-full bg-nightgreen/55 text-cream flex items-center justify-center backdrop-blur hover:bg-nightgreen transition-colors"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                {images.length > 1 && (
                  <>
                    <button
                      data-testid="modal-prev-btn"
                      onClick={prev}
                      aria-label="Previous image"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-nightgreen/55 text-cream flex items-center justify-center backdrop-blur hover:bg-nightgreen transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      data-testid="modal-next-btn"
                      onClick={next}
                      aria-label="Next image"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-nightgreen/55 text-cream flex items-center justify-center backdrop-blur hover:bg-nightgreen transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="absolute bottom-3 left-3 flex gap-2">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`modal-thumb-${i}`}
                      onClick={() => setIdx(i)}
                      aria-label={`Image ${i + 1}`}
                      className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${i === idx ? "border-gold" : "border-cream/30 opacity-70 hover:opacity-100"}`}
                    >
                      <img src={img.url} alt={img.alt || `${product.name} thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-7 sm:p-9 flex flex-col">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-[0.26em] text-gold">{product.tag || product.category}</span>
                <span
                  data-testid="modal-availability-badge"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                    comingSoon
                      ? "bg-[#F3ECE0] text-[#A87B32] border-[#DECDB3]"
                      : toConfirm
                        ? "bg-[#F3ECE0] text-soil border-[#DECDB3]"
                        : "bg-[#EBF2EC] text-forest border-[#B8D4BD]"
                  }`}
                >
                  {comingSoon && <Sprout className="w-3 h-3" />}
                  {comingSoon ? "Coming Soon" : toConfirm ? "Availability to be confirmed" : "Available"}
                </span>
              </div>
              <h3 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-forest leading-tight">{product.name}</h3>
              <p className="mt-4 text-sm sm:text-base font-light text-ink/80 leading-relaxed">{product.description}</p>
              {product.story && (
                <blockquote className="mt-5 border-l-2 border-gold pl-4 font-serif italic text-base sm:text-lg text-forest/90 leading-snug">
                  {product.story}
                </blockquote>
              )}
              {product.rehydration && (
                <div data-testid="modal-rehydration" className="mt-4 flex items-start gap-2.5 rounded-xl bg-beige border border-sand px-4 py-3">
                  <Droplets className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <p className="text-sm font-light text-ink/85">
                    <span className="font-semibold text-forest">Rehydration: </span>
                    {product.rehydration}
                  </p>
                </div>
              )}
              <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-3">
                {comingSoon ? (
                  <button
                    data-testid="modal-notify-btn"
                    onClick={() => onEnquire(product)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#A87B32] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-cream transition-all duration-400 hover:bg-gold hover:text-nightgreen"
                  >
                    <Bell className="w-4 h-4" />
                    Notify Me
                  </button>
                ) : (
                  <button
                    data-testid="modal-enquire-btn"
                    onClick={() => onEnquire(product)}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-nightgreen transition-all duration-400 hover:bg-forest hover:text-cream"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Enquire Now
                  </button>
                )}
                <button
                  data-testid="modal-contact-btn"
                  onClick={() => {
                    onClose();
                    setTimeout(() => scrollToId("contact"), 120);
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-forest/30 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-400 hover:bg-forest hover:text-cream"
                >
                  <MapPin className="w-4 h-4" />
                  Contact Farm
                </button>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {fullscreen && images.length > 0 && (
              <motion.div
                data-testid="fullscreen-viewer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[130] bg-nightgreen/95 backdrop-blur flex items-center justify-center p-3 sm:p-8"
                onClick={() => setFullscreen(false)}
              >
                <button
                  data-testid="fullscreen-close-btn"
                  onClick={() => setFullscreen(false)}
                  aria-label="Close fullscreen"
                  className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <span
                  data-testid="fullscreen-counter"
                  className="absolute bottom-5 right-5 z-10 rounded-md bg-cream/10 text-cream text-sm font-medium tracking-widest px-3 py-1.5"
                >
                  {pad(idx + 1)} / {pad(images.length)}
                </span>
                {images.length > 1 && (
                  <>
                    <button
                      data-testid="fullscreen-prev-btn"
                      onClick={(e) => { e.stopPropagation(); prev(); }}
                      aria-label="Previous image"
                      className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      data-testid="fullscreen-next-btn"
                      onClick={(e) => { e.stopPropagation(); next(); }}
                      aria-label="Next image"
                      className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
                <motion.img
                  key={`fs-${idx}`}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35 }}
                  src={images[idx].url}
                  alt={images[idx].alt || product.name}
                  className="max-h-[88vh] max-w-full rounded-xl shadow-2xl object-contain touch-pan-y"
                  drag={images.length > 1 ? "x" : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.18}
                  onDragEnd={(e, info) => {
                    e.stopPropagation?.();
                    onDragEnd(e, info);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
